import React, { useEffect, useState } from 'react';
import { InboxIcon, PlayIcon, TrashIcon } from '@heroicons/react/24/outline';
import { voicemailAPI, extensionsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const VoicemailPage = () => {
  const [extensions, setExtensions] = useState([]);
  const [selectedExtension, setSelectedExtension] = useState(null);
  const [voicemailSettings, setVoicemailSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchExtensions();
  }, []);

  const fetchExtensions = async () => {
    try {
      const response = await extensionsAPI.getAll();
      const data = response.data?.data || response.data || [];
      setExtensions(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load extensions');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVoicemailSettings = async (extensionId) => {
    try {
      const response = await voicemailAPI.getByExtension(extensionId);
      setVoicemailSettings(response.data?.data || response.data);
    } catch (error) {
      toast.error('Failed to load voicemail settings');
    }
  };

  const handleExtensionSelect = (ext) => {
    setSelectedExtension(ext);
    fetchVoicemailSettings(ext.extension);
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      await voicemailAPI.update(selectedExtension.extension, voicemailSettings);
      toast.success('Voicemail settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">Voicemail</h1>
        <p className="text-slate-400 mt-1">Configure voicemail settings for extensions</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Extension List */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Extensions</h2>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {extensions.map((ext) => (
              <button
                key={ext.extension}
                onClick={() => handleExtensionSelect(ext)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                  selectedExtension?.extension === ext.extension
                    ? 'bg-primary-500/20 text-primary-400'
                    : 'hover:bg-slate-800/50 text-slate-300'
                }`}
              >
                <div className="p-2 rounded-lg bg-slate-800">
                  <InboxIcon className="w-4 h-4" />
                </div>
                <div className="text-left">
                  <p className="font-medium">{ext.name}</p>
                  <p className="text-xs text-slate-500">{ext.extension}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Voicemail Settings */}
        <div className="lg:col-span-2">
          {selectedExtension ? (
            <div className="card">
              <h2 className="text-lg font-semibold text-white mb-4">
                Voicemail Settings - {selectedExtension.name} ({selectedExtension.extension})
              </h2>
              
              {voicemailSettings ? (
                <form onSubmit={handleSaveSettings} className="space-y-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-white">Enable Voicemail</label>
                      <p className="text-xs text-slate-500">Allow callers to leave messages</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={voicemailSettings.enabled ?? true}
                        onChange={(e) => setVoicemailSettings({...voicemailSettings, enabled: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-700 rounded-full peer peer-checked:bg-primary-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                    </label>
                  </div>

                  <div>
                    <label className="label">Voicemail PIN</label>
                    <input
                      type="password"
                      value={voicemailSettings.password || ''}
                      onChange={(e) => setVoicemailSettings({...voicemailSettings, password: e.target.value})}
                      className="input"
                      placeholder="••••"
                    />
                  </div>

                  <div>
                    <label className="label">Email for Notifications</label>
                    <input
                      type="email"
                      value={voicemailSettings.email || ''}
                      onChange={(e) => setVoicemailSettings({...voicemailSettings, email: e.target.value})}
                      className="input"
                      placeholder="user@example.com"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-white">Email Attachment</label>
                      <p className="text-xs text-slate-500">Attach voicemail as audio file</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={voicemailSettings.attach ?? true}
                        onChange={(e) => setVoicemailSettings({...voicemailSettings, attach: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-700 rounded-full peer peer-checked:bg-primary-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-white">Delete After Email</label>
                      <p className="text-xs text-slate-500">Remove message after sending email</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={voicemailSettings.delete_after_email ?? false}
                        onChange={(e) => setVoicemailSettings({...voicemailSettings, delete_after_email: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-700 rounded-full peer peer-checked:bg-primary-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                    </label>
                  </div>

                  <div className="pt-4">
                    <button type="submit" className="btn-primary">Save Settings</button>
                  </div>
                </form>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <div className="spinner" />
                </div>
              )}
            </div>
          ) : (
            <div className="card text-center py-12">
              <InboxIcon className="w-12 h-12 mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400">Select an extension to manage voicemail</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoicemailPage;
